import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '../../../stories/atoms';
import { Charts } from '../../../stories/molecules/charts/Charts';
import { profitAndLossTransalations } from '../../../translations';
import { StyledPLCardContainer } from '../ProfitAndLoss.styles';
import { getFinancialYearDisplayLabel } from '../util';
import { ProfitLossChartData } from './profitLossChartUtils';

type ProfitLossChartSectionProps = {
  showPLChart: boolean;
  selectedYear: string;
  financialYear: string;
  profitLossChartData: ProfitLossChartData | null;
  isMobile: boolean;
};

export const ProfitLossChartSection: FC<ProfitLossChartSectionProps> = ({
  showPLChart,
  selectedYear,
  financialYear,
  profitLossChartData,
  isMobile,
}) => {
  const { t } = useTranslation();
  const { chartTitle, chartValueProfit, chartValueLoss, expenses, costNames } = profitAndLossTransalations;

  if (!showPLChart) {
    return null;
  }

  return (
    <StyledPLCardContainer
      isParent
      padding={[3]}
      style={{
        minHeight: '350px',
      }}
      margin={[0, 0, 4, 0]}
    >
      <Typography type="h4" margin={[0, 0, 3, 0]} data-testid="profit-loss-chart-title">
        {t(chartTitle.key, chartTitle.defaultValue)} -{' '}
        {getFinancialYearDisplayLabel(selectedYear || financialYear)}
      </Typography>
      {profitLossChartData && (
        <Charts
          chartType="bar"
          xAxisCategories={profitLossChartData.xAxisCategories}
          chartSeries={profitLossChartData.chartSeries}
          chartColors={profitLossChartData.chartColors}
          isAnimationEnabled={true}
          showGridYAxis={true}
          legend={{
            show: false,
            position: 'top',
            horizontalAlign: 'left',
          }}
          yAxis={{
            labels: {
              formatter: function (value) {
                const absValue = Math.abs(value);
                if (absValue >= 1000000) {
                  return (value / 1000000).toFixed(1) + 'M';
                }
                if (absValue >= 1000) {
                  return (value / 1000).toFixed() + 'K';
                }
                return value.toFixed();
              },
            },
          }}
          toolTip={{
            cssClass: 'custom-apex-tooltip',
            x: {
              show: true,
              formatter: (_value, { dataPointIndex }) => {
                const netAmount = profitLossChartData.chartSeries[0]?.data[dataPointIndex] ?? 0;
                const profitLossLabel =
                  netAmount >= 0
                    ? t(chartValueProfit.key, chartValueProfit.defaultValue)
                    : t(chartValueLoss.key, chartValueLoss.defaultValue);

                return `${profitLossLabel}: ${Math.abs(Number(netAmount)).toLocaleString()}`;
              },
            },
            y: {
              title: {
                formatter() {
                  return '';
                },
              },
              formatter: function (_value, context) {
                const dataPointIndex = context.dataPointIndex;
                const breakdown = profitLossChartData.tooltipBreakdown[dataPointIndex] ?? {
                  salesExclVat: 0,
                  expenses: 0,
                  surplus: 0,
                };

                const expensesLabel = t(expenses.key, expenses.defaultValue);
                const salesExclVatLabel = t(costNames.salesExcVat.key, costNames.salesExcVat.defaultValue);
                const surplusLabel = t(costNames.surplus1.key, costNames.surplus1.defaultValue);
                const formatNumber = (num: number) => num.toLocaleString();

                return [
                  `${expensesLabel}: ${formatNumber(breakdown.expenses)}`,
                  `${salesExclVatLabel}: ${formatNumber(breakdown.salesExclVat)}`,
                  `${surplusLabel}: ${formatNumber(breakdown.surplus)}`,
                ]
                  .map((line) => `<div style="font-weight: 400;">${line}</div>`)
                  .join('');
              },
            },
          }}
          areaChartGradient={{ opacity: 1 }}
          chartPlotOptions={{
            distributed: true,
            columnWidth: isMobile ? '8px' : '24px',
            borderRadius: 0,
          }}
          dataLabel={{
            enabled: false,
          }}
          data-testid="profit-loss-bar-chart"
        />
      )}
    </StyledPLCardContainer>
  );
};
